import { useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useAppSelector, useComparisonPeriod, ComparisonPeriod, useWeekRange, WeekRange } from 'src/utils/Hooks';
import {
  getComparisonWeekId,
  getPreviousYearWeekId,
  getFirstWeekIdOfYear,
  getLastWeekIdOfYear,
  yearPartOfWeekId,
  getNextWeekId
} from 'src/utils/dateUtils';

/**
 * Generates all week IDs between start and end (inclusive)
 */
const generateWeekIdRange = (startWeekId: number, endWeekId: number): number[] => {
  if (!startWeekId || !endWeekId || startWeekId > endWeekId) {
    return [];
  }

  const weekIds: number[] = [];
  let current = startWeekId;

  while (current <= endWeekId) {
    weekIds.push(current);
    current = getNextWeekId(current);
  }

  return weekIds;
};

/**
 * Return type for useComparisonWeekIds hook
 */
export interface UseComparisonWeekIdsResult {
  /** Start week ID for the primary period */
  startWeekId: number;
  /** End week ID for the primary period */
  endWeekId: number;
  /** Start week ID for the comparison period (API-based) */
  comparisonStartWeekId: number;
  /** End week ID for the comparison period (API-based) */
  comparisonEndWeekId: number;
  /** Whether the comparison week IDs are loading from the API */
  isLoadingComparisonWeeks: boolean;
  /** Whether this is YTD with Prior Year comparison */
  isYTDWithPriorYear: boolean;
  /** Whether this is Last Week with Prior Year comparison */
  isLastWeekWithPriorYear: boolean;
  /**
   * Comparison week IDs that correspond to each main period week ID.
   * These are fetched from the API and properly map 52/53 week years.
   */
  comparisonWeekIdsForMainPeriod: number[];
}

/**
 * Hook that provides comparison week IDs using the microservice API.
 * 
 * Uses the same pattern as useForecastComparisonStartAndEndWeekIds:
 * - Fetches comparison weeks from the API using useQuery
 * - Falls back to local calculation if API fails
 * - Uses placeholderData for instant display while loading
 * 
 * For Prior Year comparisons, the API provides the correct week mapping
 * that accounts for 52/53 week years.
 */
export const useComparisonWeekIds = (): UseComparisonWeekIdsResult => {
  const weekRange = useWeekRange();
  const comparisonPeriod = useComparisonPeriod();

  // Get main time period from Redux
  const defaultStartWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const endWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);
  const defaultComparisonStartWeekId = useAppSelector((state) => state.comparisonTimePeriod.startWeek);
  const defaultComparisonEndWeekId = useAppSelector((state) => state.comparisonTimePeriod.endWeek);

  // Special case: comparing last week with prior year shows YTD data
  const isLastWeekWithPriorYear = useMemo(
    () => weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR,
    [weekRange, comparisonPeriod]
  );

  // YTD with Prior Year - we show full prior year comparison data
  const isYTDWithPriorYear = useMemo(
    () => weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR,
    [weekRange, comparisonPeriod]
  );

  // For Last Week + Prior Year, expand main period to YTD
  const startWeekId = useMemo(() => {
    if (isLastWeekWithPriorYear) {
      return getFirstWeekIdOfYear(yearPartOfWeekId(endWeekId));
    }
    return defaultStartWeekId;
  }, [isLastWeekWithPriorYear, endWeekId, defaultStartWeekId]);

  // Determine if we need to fetch from API (Prior Year comparisons)
  const shouldUsePriorYearApi = comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;

  // Memoize placeholder data (local calculation fallback)
  const startPlaceholder = useMemo(
    () => (startWeekId > 0 ? getPreviousYearWeekId(startWeekId) : undefined),
    [startWeekId]
  );
  const endPlaceholder = useMemo(
    () => (endWeekId > 0 ? getPreviousYearWeekId(endWeekId) : undefined),
    [endWeekId]
  );

  // Fetch comparison week for start - use placeholderData for instant display
  const { data: comparisonStartFromApi, isLoading: isLoadingStart } = useQuery({
    queryKey: ['comparisonWeek', 'start', startWeekId],
    queryFn: () => getComparisonWeekId(startWeekId),
    enabled: shouldUsePriorYearApi && startWeekId > 0,
    staleTime: 60 * 60 * 1000, // 1 hour - same as the library cache
    refetchOnWindowFocus: false,
    placeholderData: startPlaceholder
  });

  // Fetch comparison week for end - use placeholderData for instant display
  const { data: comparisonEndFromApi, isLoading: isLoadingEnd } = useQuery({
    queryKey: ['comparisonWeek', 'end', endWeekId],
    queryFn: () => getComparisonWeekId(endWeekId),
    enabled: shouldUsePriorYearApi && endWeekId > 0,
    staleTime: 60 * 60 * 1000, // 1 hour - same as the library cache
    refetchOnWindowFocus: false,
    placeholderData: endPlaceholder
  });

  // Generate all week IDs in the main period for batch fetching
  const mainPeriodWeekIds = useMemo(
    () => generateWeekIdRange(startWeekId, endWeekId),
    [startWeekId, endWeekId]
  );

  // Batch fetch comparison week IDs for all main period weeks
  // This properly handles 52/53 week year mappings through the API
  const comparisonWeekQueries = useQueries({
    queries: mainPeriodWeekIds.map((weekId) => ({
      queryKey: ['comparisonWeek', weekId],
      queryFn: () => getComparisonWeekId(weekId),
      enabled: shouldUsePriorYearApi && weekId > 0,
      staleTime: 60 * 60 * 1000, // 1 hour cache
      refetchOnWindowFocus: false,
      // Use local calculation as placeholder for instant display
      placeholderData: getPreviousYearWeekId(weekId)
    }))
  });

  // Extract the comparison week IDs from the query results
  const comparisonWeekIdsForMainPeriod = useMemo(() => {
    if (!shouldUsePriorYearApi) {
      // For non-Prior Year comparisons (like Prior Period), use the actual comparison period weeks from Redux
      // The comparison period is stored in Redux and represents the correct prior period range
      return generateWeekIdRange(defaultComparisonStartWeekId, defaultComparisonEndWeekId);
    }

    return comparisonWeekQueries
      .map((query, index) => query.data ?? getPreviousYearWeekId(mainPeriodWeekIds[index]))
      .filter((weekId): weekId is number => weekId !== undefined && weekId > 0);
  }, [shouldUsePriorYearApi, comparisonWeekQueries, mainPeriodWeekIds, defaultComparisonStartWeekId, defaultComparisonEndWeekId]);

  const isLoadingBatchComparisonWeeks = comparisonWeekQueries.some((query) => query.isLoading);
  const isLoadingComparisonWeeks = isLoadingStart || isLoadingEnd || isLoadingBatchComparisonWeeks;

  // Calculate comparison week IDs
  const comparisonStartWeekId = useMemo(() => {
    if (!shouldUsePriorYearApi) {
      // For non-Prior Year comparisons, use Redux values
      return defaultComparisonStartWeekId;
    }

    if (isLastWeekWithPriorYear) {
      // Start from the API-calculated comparison for first week of current year
      return comparisonStartFromApi ?? getPreviousYearWeekId(startWeekId);
    }

    // Use API-calculated comparison week, fall back to local calculation
    return comparisonStartFromApi ?? getPreviousYearWeekId(startWeekId);
  }, [
    shouldUsePriorYearApi,
    isLastWeekWithPriorYear,
    comparisonStartFromApi,
    startWeekId,
    defaultComparisonStartWeekId
  ]);

  const comparisonEndWeekId = useMemo(() => {
    if (!shouldUsePriorYearApi) {
      // For non-Prior Year comparisons, use Redux values
      return defaultComparisonEndWeekId;
    }

    if (isYTDWithPriorYear) {
      // For YTD + Prior Year, get the last week of the comparison year
      const comparisonYear = yearPartOfWeekId(comparisonStartWeekId);
      return getLastWeekIdOfYear(comparisonYear);
    }

    if (isLastWeekWithPriorYear) {
      // For Last Week + Prior Year, show full prior year
      const comparisonYear = yearPartOfWeekId(endWeekId) - 1;
      return getLastWeekIdOfYear(comparisonYear);
    }

    // Use API-calculated comparison week, fall back to local calculation
    return comparisonEndFromApi ?? getPreviousYearWeekId(endWeekId);
  }, [
    shouldUsePriorYearApi,
    isYTDWithPriorYear,
    isLastWeekWithPriorYear,
    comparisonStartWeekId,
    comparisonEndFromApi,
    endWeekId,
    defaultComparisonEndWeekId
  ]);

  return {
    startWeekId,
    endWeekId,
    comparisonStartWeekId,
    comparisonEndWeekId,
    isLoadingComparisonWeeks,
    isYTDWithPriorYear,
    isLastWeekWithPriorYear,
    comparisonWeekIdsForMainPeriod
  };
};

export default useComparisonWeekIds;
